import {FancyPage, useProjectSidebar} from '@wandb/weave/components/FancyPage';
import {useProjectHasTraceServerData} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import React, {memo, useContext, useEffect, useRef} from 'react';
import {Redirect, Route, RouteProps, Switch, useRouteMatch} from 'react-router';
import {useLocation} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import LaunchPage from '../../components/Launch';
import {JobsPage} from '../../components/Launch/Jobs/JobsPage';
import {useAccountOrganizationOrPersonalEntityUsingLaunch} from '../../components/Launch/launchActive';
import {TriggersPage} from '../../components/Launch/Triggers/TriggersPage';
import NoMatch from '../../components/NoMatch';
import {isEmbeddedPanelPath} from '../../components/Panel/utils';
import ProjectArtifactsTab from '../../components/ProjectArtifactsTab';
import ProjectOverview from '../../components/ProjectOverview/ProjectOverview';
import {ProjectWeaveTab} from '../../components/ProjectWeaveTab';
import {
  QuickStart,
  QuickStartAnalyticsOrigin,
} from '../../components/QuickStart';
import {checkRegistryProject} from '../../components/Registries/common/utils';
import {IMPORT_PANEL_QS} from '../../components/ReportPanelGridSection';
import ReportTableWithQuery from '../../components/ReportTable';
import {AccountSelectorContext} from '../../components/Search/SearchNav/AccountSelectorContextProvider';
import {getNodeFromValue} from '../../components/Slate/SlashMenu';
import SweepsTable from '../../components/SweepsTable';
import {envIsLocal, envIsPublicCloud, hasTraceBackend} from '../../config';
import {LoaderProxy} from '../../containers/AppLoader';
import {
  PROJECT_BASE_PATH,
  PROJECT_OVERVIEW_PATH,
  WEAVE_SLUG,
} from '../../routes/paths';
import {Analytics} from '../../services/analytics';
import * as PollingActions from '../../state/polling/actions';
import {useViewer} from '../../state/viewer/hooks';
import {useAdminModeActive} from '../../util/admin';
import {getEntityNameFromPath} from '../../util/cache';
import {usePersistentRumViewContext} from '../../util/datadog';
import history from '../../util/history';
import {AccessOption, getProjectAccess} from '../../util/permissions';
import {usePerformanceStatLogger} from '../../util/profiler/react';
import {useRampFlagEmbeddedPanels} from '../../util/rampFeatureFlags';
import * as Report from '../../util/report';
import {useViewerHasRestrictedProjectView} from '../../util/restrictedProjectView';
import * as urls from '../../util/urls';
import {useModelsAccess} from '../../util/useModelsAccess';
import {useWeaveAccess} from '../../util/useWeaveAccess';
import {checkTheme, getTheme} from '../Benchmark/Theme';
import JobPage from '../JobPage';
import NoAccessPage from '../NoAccessPage';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../OrgDashboard/OrgRoles/RolesDashboard';
import {NoRunsSampleScript} from '../ProjectPageOnboarding';
import {ProjectPageContextProvider} from './ProjectPageContext';
import {shouldRedirectToWeaveTraces} from './shouldRedirectToWeaveTraces';
import {useDocumentMeta} from './useDocumentMeta';
import {useProjectPageProps} from './useProjectPageProps';
import {useProjectParams} from './useProjectParams';
import {WorkspaceTabBody} from './WorkspaceTabBody';
const StyledProjectPage = styled.div<{hideSidebar?: boolean}>`
  & > div.fancy-page > div.fancy-page__sidebar {
    ${props => (props.hideSidebar ? 'width: 0px; overflow: hidden;' : '')}
    transition: width 0.2s linear;
  }
`;
StyledProjectPage.displayName = 'StyledProjectPage';

const MIN_POLL_INTERVAL = 10000;
const MAX_POLL_INTERVAL = 60000;
interface RouteWithAccessProps extends RouteProps {
  children: React.ReactNode;
  isPublicOrOpenProject: boolean;
}
const RouteWithModelsAccess: React.FC<RouteWithAccessProps> = ({
  children,
  isPublicOrOpenProject,
  ...props
}) => {
  const {hasModelsAccess, isOrgMemberLoading} = useModelsAccess(
    isPublicOrOpenProject
  );
  if (!isOrgMemberLoading && !hasModelsAccess) {
    return <NoAccessPage contentKey={'noModelsAccess'} />;
  }

  return <Route {...props}>{children}</Route>;
};

const RouteWithWeaveAccess: React.FC<RouteWithAccessProps> = ({
  children,
  isPublicOrOpenProject,
  ...props
}) => {
  const isAdminMode = useAdminModeActive();
  const {entityName} = useProjectParams();
  const {isWeaveLimitReached, isWeaveEnterpriseTrialExpired, isLoading} =
    useWeaveAccess(isPublicOrOpenProject, isAdminMode, entityName);
  if (isLoading) {
    return <LoaderProxy name="project-page" />;
  }
  if (isWeaveLimitReached) {
    return <NoAccessPage contentKey={'weaveLimitReached'} />;
  }
  if (isWeaveEnterpriseTrialExpired) {
    return <NoAccessPage contentKey={'weaveTrialExpired'} />;
  }
  return <Route {...props}>{children}</Route>;
};

const ProjectPageComp = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const isEmbeddedPanel =
    useRampFlagEmbeddedPanels(getEntityNameFromPath()) &&
    isEmbeddedPanelPath(currentPath);

  const {dispatch, projectQuery, pushDraftReport} = useProjectPageProps();

  const {
    artifactTypeName,
    artifactCollectionName,
    artifactCommitHash,
    artifactCollectionView,
    artifactTab,
    entityName,
    launchTab,
    filePath,
    projectName,
    tab,
    weaveTab,
  } = useProjectParams();

  const projectHasTraceServerData = useProjectHasTraceServerData(
    entityName,
    projectName
  );
  const hasWeaveData = projectHasTraceServerData.result;

  const pollIntervalSet = useRef(false);

  useEffect(() => {
    if (
      !projectQuery.loading &&
      projectQuery.data?.project !== null &&
      !pollIntervalSet.current
    ) {
      // Scale poll interval from 10s (when there are 0 runs) to 60s
      // (when there are 5000 runs).
      let pollInterval =
        (((projectQuery.data?.project.totalRuns ?? 0) / 5000) * 50 + 10) * 1000;
      if (pollInterval < MIN_POLL_INTERVAL) {
        pollInterval = MIN_POLL_INTERVAL;
      }
      if (pollInterval > MAX_POLL_INTERVAL) {
        pollInterval = MAX_POLL_INTERVAL;
      }
      dispatch(PollingActions.setBasePollInterval(pollInterval));
      pollIntervalSet.current = true;
    }
  });

  // Clear polling on unmount
  useEffect(() => {
    if (!pollIntervalSet.current) {
      return;
    }
    return () => {
      dispatch(PollingActions.setBasePollInterval(0));
    };
  }, []); // eslint-disable-line

  const {selectedAccount} = useContext(AccountSelectorContext);
  const accountOrgName = envIsPublicCloud
    ? selectedAccount?.name ?? ''
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const project = !projectQuery.loading ? projectQuery.data?.project : null;
  const runCount = project?.totalRuns ?? 0;
  const hasModelsData = runCount > 0;
  const isLoading = projectQuery.loading || projectHasTraceServerData.loading;

  const viewingRestricted = useViewerHasRestrictedProjectView({
    projectAccess: getProjectAccess(project?.access ?? AccessOption.Private),
    projectName,
    entityName,
  });

  const isPublicOrOpenProject =
    project?.access === AccessOption.Public ||
    project?.access === AccessOption.Open;
  const {hasModelsAccess, isOrgMemberLoading} = useModelsAccess(
    isPublicOrOpenProject
  );

  const {isLaunchActive} = useAccountOrganizationOrPersonalEntityUsingLaunch();
  const viewer = useViewer();
  const isWandbAdmin = !envIsLocal && (viewer?.admin ?? false);

  const sidebarItems = useProjectSidebar(
    isLoading,
    viewingRestricted,
    hasModelsData,
    hasWeaveData,
    hasTraceBackend,
    hasModelsAccess,
    isLaunchActive,
    isWandbAdmin
  );

  useDocumentMeta({
    reportCount: project?.reports?.totalCount ?? 0,
    runCount,
    sweepCount: project?.totalSweeps ?? 0,
  });

  usePerformanceStatLogger(
    'workspace.runs_count',
    project?.totalRuns ?? 0,
    v => v >= 500
  );
  usePersistentRumViewContext(
    'workspace.runs_count',
    project?.totalRuns ?? null
  );

  const match = useRouteMatch(PROJECT_OVERVIEW_PATH);

  if (isLoading || isOrgMemberLoading) {
    return <LoaderProxy name="project-page" />;
  }

  const {isRegistryProject, registryName} = checkRegistryProject(projectName);
  if (isRegistryProject) {
    return <Redirect to={urls.globalRegistry(accountOrgName, registryName)} />;
  }

  if (shouldRedirectToWeaveTraces(tab, hasWeaveData)) {
    return (
      <Redirect
        to={urls.weaveView({entityName, projectName, weaveTab: 'traces'})}
      />
    );
  }

  if (!project) {
    return <NoMatch />;
  }

  // Team admins can view some details of restricted projects if they are not explicit members. Redirect them to the
  // project overview page, where they can view project members and add themselves
  if (viewingRestricted && !match) {
    return <Redirect to={urls.projectOverview(entityName, projectName)} />;
  }

  if (project.entity.claimingEntity != null) {
    // If the project's entity has been claimed, redirect to the claiming
    // entity instead so the original link still works.
    return (
      <Redirect
        to={urls.project({
          entityName: project.entity.claimingEntity.name,
          name: projectName,
        })}
      />
    );
  }

  if (
    project.isBenchmark &&
    checkTheme({entityName, name: projectName, views: project.views})
  ) {
    return (
      <Redirect to={urls.benchmarkProject({entityName, name: projectName})} />
    );
  }

  const linkedBenchmarkMeta =
    !project.isBenchmark && project.linkedBenchmark != null
      ? project.linkedBenchmark
      : null;

  const linkedBenchmarkInfo =
    linkedBenchmarkMeta != null ? getTheme(linkedBenchmarkMeta) : null;

  if (project.totalRuns === 0 && projectName === 'sample-project') {
    return <NoRunsSampleScript key="runsTab" />;
  }

  const baseUrl = urls.project({entityName, name: projectName});

  const onSetTableExpanded = (isTableExpanded: boolean) => {
    history.push({
      pathname: baseUrl + (isTableExpanded ? '/table' : '/workspace'),
      search: window.location.search,
    });
  };

  let activeSlug = tab;
  if (weaveTab) {
    if ('calls' === weaveTab) {
      activeSlug = `${tab}/traces`;
    } else if ('ops' === weaveTab) {
      activeSlug = `${tab}/operations`;
    } else {
      activeSlug = `${tab}/${weaveTab}`;
    }
  }

  return (
    <ProjectPageContextProvider project={project}>
      <StyledProjectPage hideSidebar={isEmbeddedPanel} className="project-page">
        <FancyPage
          baseUrl={baseUrl}
          activeSlug={activeSlug ?? 'workspace'}
          items={sidebarItems}>
          <Switch>
            <Route
              path={[
                `${PROJECT_BASE_PATH}/overview`,
                `${PROJECT_BASE_PATH}/overview/details`,
                `${PROJECT_BASE_PATH}/overview/users`,
                `${PROJECT_BASE_PATH}/overview/providers`,
              ]}>
              <ProjectOverview
                project={project}
                hasWeaveData={hasWeaveData}
                hasModelsData={hasModelsData}
              />
            </Route>
            <RouteWithModelsAccess
              path={[
                PROJECT_BASE_PATH,
                `${PROJECT_BASE_PATH}/workspace`,
                `${PROJECT_BASE_PATH}/workspace/panel/:panelId`,
                `${PROJECT_BASE_PATH}/table`,
                `${PROJECT_BASE_PATH}/panel/:panelId`,
                `${PROJECT_BASE_PATH}/embed/panel/:panelId`,
              ]}
              exact
              isPublicOrOpenProject={isPublicOrOpenProject}>
              {runCount === 0 ? (
                <QuickStart
                  analyticsOrigin={QuickStartAnalyticsOrigin.PROJECT_PAGE}
                />
              ) : (
                <WorkspaceTabBody
                  isTableExpanded={tab === 'table'}
                  linkedBenchmarkInfo={linkedBenchmarkInfo}
                  onSetTableExpanded={onSetTableExpanded}
                  runCount={runCount}
                />
              )}
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/launch`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <LaunchPage
                tab={launchTab}
                projectLoading={projectQuery.loading}
                entityName={entityName}
                project={project}
                refetchProject={projectQuery.refetch}
              />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={[
                `${PROJECT_BASE_PATH}/jobs/:jobId/:jobTab/:jobVersion?`,
                `${PROJECT_BASE_PATH}/jobs/:jobId/:jobTab?`,
              ]}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <JobPage />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/jobs`}
              exact
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <JobsPage entityName={entityName} projectName={projectName} />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/automations`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <TriggersPage entityName={entityName} project={project} />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/sweeps`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <SweepsTable entityName={entityName} projectName={projectName} />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/reportlist`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <ReportTableWithQuery
                entityName={entityName}
                projectName={projectName}
                runCount={runCount}
                onCreateNewReport={() => {
                  Analytics.track('Create Report Started', {
                    location: `reports tab`,
                    entityName,
                    projectName,
                  });
                  const report = Report.getEmptyReportConfig();
                  pushDraftReport(report);
                }}
                onCreateReportPrompt={() => {
                  Analytics.track('Create Report Started', {
                    location: `create report prompt reports tab`,
                    entityName,
                    projectName,
                  });
                  const report = Report.getEmptyReportConfig([
                    getNodeFromValue(`panel-grid`),
                  ]);
                  pushDraftReport(report, undefined, {
                    redirectQS: {[IMPORT_PANEL_QS]: null},
                  });
                }}
              />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/artifacts`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <ProjectArtifactsTab
                entityName={entityName}
                projectName={projectName}
                history={history}
                artifactTypeName={artifactTypeName}
                artifactCollectionName={artifactCollectionName}
                artifactCommitHash={artifactCommitHash}
                artifactCollectionView={artifactCollectionView}
                artifactTab={artifactTab}
                filePath={filePath}
              />
            </RouteWithModelsAccess>
            <RouteWithWeaveAccess
              path={`${PROJECT_BASE_PATH}/${WEAVE_SLUG}`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <ProjectWeaveTab
                entityName={entityName}
                projectName={projectName}
              />
            </RouteWithWeaveAccess>
          </Switch>
        </FancyPage>
      </StyledProjectPage>
    </ProjectPageContextProvider>
  );
};

export const ProjectPage = memo(ProjectPageComp);
